import React, { Component } from "react";
import * as XLSX from "xlsx";
import {
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteField,
} from "firebase/firestore";
import { db } from "../../Config/Config";
import Header from "../layout/Header";

// ─── Font ─────────────────────────────────────────────────────────
const _fl = document.createElement("link");
_fl.rel = "stylesheet";
_fl.href =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600;700&display=swap";
document.head.appendChild(_fl);

// ─── Tokens ──────────────────────────────────────────────────────
const C = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  chrome: "#1A1F2E",
  chromeMid: "#252B3B",
  indigo: "#4F46E5",
  indigoLight: "#EEF2FF",
  emerald: "#10B981",
  emeraldBg: "#ECFDF5",
  amber: "#F59E0B",
  amberBg: "#FFFBEB",
  rose: "#F43F5E",
  roseBg: "#FFF1F2",
  slate: "#475569",
  muted: "#94A3B8",
  border: "#E5E7EB",
  text: "#111827",
  navy: "#0B1D3A",
  navyMid: "#162d57",
  gold: "#C9A84C",
  goldLight: "#f0e0a8",
  manualTag: "#7C3AED",
  manualTagBg: "#F3E8FF",
  bioTag: "#4F46E5",
  bioTagBg: "#EEF2FF",
};

const F = {
  ui: "'Inter', system-ui, sans-serif",
  display: "'Playfair Display', Georgia, serif",
};

// ─── Firestore locations ────────────────────────────────────────────
// Biometric (Excel import) data
const BIO_COLLECTION = "NDCS";
const BIO_DOC_ID = "BiometricAttendance";
// Manual entry data
const MANUAL_COLLECTION = "NDCM";
const MANUAL_DOC_ID = "PGAttendance";

// ─── Styles ───────────────────────────────────────────────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .bd-page { font-family: ${F.ui}; background: ${C.bg}; min-height: 100vh; color: ${C.text}; }

  /* NDC banner header */
  .bd-banner {
    background: linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%);
    padding: 48px 24px 40px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }
  .bd-banner::before {
    content: '';
    position: absolute; inset: 0;
    background: repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 28px,
      rgba(201,168,76,.05) 28px,
      rgba(201,168,76,.05) 29px
    );
    pointer-events: none;
  }
  .bd-banner-eyebrow {
    font-family: ${F.ui};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: ${C.gold};
    margin-bottom: 10px;
    position: relative;
  }
  .bd-banner-title {
    font-family: ${F.display};
    font-size: clamp(26px, 4.5vw, 42px);
    font-weight: 700;
    color: #fff;
    margin: 0 0 10px;
    line-height: 1.2;
    position: relative;
  }
  .bd-banner-sub {
    color: rgba(255,255,255,.65);
    font-size: 15px;
    margin: 0;
    position: relative;
  }

  /* Top bar */
  .bd-topbar {
    background: ${C.chrome};
    padding: 0 28px;
    height: 64px;
    display: flex; align-items: center; justify-content: space-between;
    position: sticky; top: 0; z-index: 200;
    border-bottom: 1px solid rgba(255,255,255,.06);
  }
  .bd-topbar-title { font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -.2px; }
  .bd-topbar-sub { font-size: 12px; color: rgba(255,255,255,.4); margin-top: 1px; }

  /* Import button */
  .bd-import-btn {
    display: inline-flex; align-items: center; gap: 8px;
    background: ${C.indigo}; color: #fff;
    padding: 9px 18px; border-radius: 8px;
    font-size: 13px; font-weight: 600; font-family: ${F.ui};
    cursor: pointer; border: none;
    transition: background .15s, transform .1s;
    white-space: nowrap;
  }
  .bd-import-btn:hover { background: #4338CA; }
  .bd-import-btn:active { transform: scale(.97); }

  /* Stat pills */
  .bd-stat { display: flex; flex-direction: column; align-items: flex-end; }
  .bd-stat-val { font-size: 18px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
  .bd-stat-lbl { font-size: 10px; font-weight: 500; letter-spacing: .8px; text-transform: uppercase; color: rgba(255,255,255,.4); margin-top: 2px; }

  /* Main */
  .bd-main { max-width: 1200px; margin: 0 auto; padding: 28px 24px 80px; }

  /* Filter bar */
  .bd-filters {
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 16px 20px;
    display: flex; gap: 12px; flex-wrap: wrap; align-items: center;
    margin-bottom: 28px;
  }
  .bd-input {
    flex: 1; min-width: 180px;
    padding: 9px 14px;
    border: 1.5px solid ${C.border};
    border-radius: 8px;
    font-family: ${F.ui}; font-size: 13.5px; color: ${C.text};
    outline: none; background: ${C.bg};
    transition: border-color .15s;
  }
  .bd-input:focus { border-color: ${C.indigo}; background: #fff; }
  .bd-select {
    flex: 1; min-width: 160px;
    padding: 9px 14px;
    border: 1.5px solid ${C.border};
    border-radius: 8px;
    font-family: ${F.ui}; font-size: 13.5px; color: ${C.text};
    outline: none; background: ${C.bg}; cursor: pointer;
    transition: border-color .15s;
  }
  .bd-select:focus { border-color: ${C.indigo}; }
  .bd-range-wrap { display: flex; flex-direction: column; gap: 4px; min-width: 160px; }
  .bd-range-lbl { font-size: 11px; font-weight: 600; color: ${C.muted}; letter-spacing: .5px; }
  .bd-range { width: 100%; accent-color: ${C.indigo}; }
  .bd-source-toggle { display: flex; gap: 6px; }
  .bd-source-chip {
    padding: 8px 14px; border-radius: 8px;
    border: 1.5px solid ${C.border}; background: ${C.bg};
    font-family: ${F.ui}; font-size: 12.5px; font-weight: 600; color: ${C.slate};
    cursor: pointer; transition: all .15s; white-space: nowrap;
  }
  .bd-source-chip.active { border-color: ${C.indigo}; background: ${C.indigoLight}; color: ${C.indigo}; }

  /* Section heading */
  .bd-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .bd-section-title { font-size: 14px; font-weight: 700; color: ${C.slate}; letter-spacing: .3px; text-transform: uppercase; }

  /* Batch accordion */
  .bd-batch { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 12px; overflow: hidden; margin-bottom: 12px; transition: box-shadow .2s; }
  .bd-batch:hover { box-shadow: 0 4px 16px rgba(0,0,0,.06); }
  .bd-batch-header { padding: 16px 20px; display: flex; align-items: center; gap: 16px; cursor: pointer; user-select: none; border: none; background: none; width: 100%; font-family: ${F.ui}; text-align: left; }
  .bd-batch-header:hover { background: #FAFAFA; }
  .bd-batch-chevron { width: 28px; height: 28px; border-radius: 6px; background: ${C.bg}; border: 1px solid ${C.border}; display: flex; align-items: center; justify-content: center; color: ${C.muted}; flex-shrink: 0; transition: transform .2s, background .15s; }
  .bd-batch-chevron.open { transform: rotate(180deg); background: ${C.indigoLight}; color: ${C.indigo}; border-color: ${C.indigo}; }
  .bd-batch-meta { flex: 1; min-width: 0; }
  .bd-batch-date { font-size: 13.5px; font-weight: 600; color: ${C.text}; display: flex; align-items: center; gap: 8px; }
  .bd-batch-count { font-size: 12px; color: ${C.muted}; margin-top: 2px; }

  .bd-source-badge {
    font-size: 10px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase;
    padding: 2px 8px; border-radius: 999px;
  }
  .bd-source-badge.manual { color: ${C.manualTag}; background: ${C.manualTagBg}; }
  .bd-source-badge.biometric { color: ${C.bioTag}; background: ${C.bioTagBg}; }

  /* Mini attendance bar in header */
  .bd-mini-bar-wrap { display: flex; flex-direction: column; gap: 4px; min-width: 120px; }
  .bd-mini-bar-lbl { font-size: 10px; font-weight: 600; letter-spacing: .5px; text-transform: uppercase; color: ${C.muted}; display: flex; justify-content: space-between; }
  .bd-mini-bar-track { height: 4px; background: ${C.border}; border-radius: 2px; overflow: hidden; }
  .bd-mini-bar-fill { height: 100%; border-radius: 2px; transition: width .4s ease; }

  .bd-delete-btn {
    padding: 6px 12px; border-radius: 7px;
    border: 1.5px solid #FECDD3; background: transparent;
    font-size: 12px; font-weight: 600; color: ${C.rose};
    font-family: ${F.ui}; cursor: pointer; flex-shrink: 0;
    transition: background .15s;
  }
  .bd-delete-btn:hover { background: ${C.roseBg}; }

  /* Table */
  .bd-table-wrap { overflow-x: auto; }
  .bd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .bd-table th { padding: 10px 14px; text-align: left; font-size: 10.5px; font-weight: 700; letter-spacing: .7px; text-transform: uppercase; color: ${C.muted}; background: ${C.bg}; border-bottom: 1px solid ${C.border}; white-space: nowrap; }
  .bd-table td { padding: 11px 14px; border-bottom: 1px solid ${C.border}; color: ${C.slate}; vertical-align: middle; }
  .bd-table tr:last-child td { border-bottom: none; }
  .bd-table tr:hover td { background: #FAFAFA; }

  /* Inline attendance bar */
  .bd-att-cell { min-width: 130px; }
  .bd-att-row { display: flex; align-items: center; gap: 8px; }
  .bd-att-track { flex: 1; height: 5px; background: ${C.border}; border-radius: 3px; overflow: hidden; }
  .bd-att-fill { height: 100%; border-radius: 3px; }
  .bd-att-pct { font-size: 12px; font-weight: 600; font-variant-numeric: tabular-nums; min-width: 38px; text-align: right; }

  /* Empty */
  .bd-empty { text-align: center; padding: 48px 20px; color: ${C.muted}; }
  .bd-empty-icon { font-size: 32px; margin-bottom: 10px; }

  /* Modal */
  .bd-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; padding: 20px; }
  .bd-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 900px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 24px 64px rgba(0,0,0,.18); }
  .bd-modal-head { padding: 20px 24px; border-bottom: 1px solid ${C.border}; display: flex; align-items: center; justify-content: space-between; }
  .bd-modal-title { font-size: 16px; font-weight: 700; color: ${C.text}; }
  .bd-modal-close { width: 32px; height: 32px; border-radius: 8px; border: 1.5px solid ${C.border}; background: transparent; cursor: pointer; font-size: 16px; color: ${C.muted}; display: flex; align-items: center; justify-content: center; transition: background .15s; }
  .bd-modal-close:hover { background: ${C.bg}; }
  .bd-modal-body { flex: 1; overflow-y: auto; padding: 0; }
  .bd-modal-foot { padding: 16px 24px; border-top: 1px solid ${C.border}; display: flex; justify-content: flex-end; gap: 10px; }
  .bd-btn-secondary { padding: 9px 18px; border-radius: 8px; border: 1.5px solid ${C.border}; background: transparent; font-family: ${F.ui}; font-size: 13px; font-weight: 600; color: ${C.slate}; cursor: pointer; transition: background .15s; }
  .bd-btn-secondary:hover { background: ${C.bg}; }
  .bd-btn-primary { padding: 9px 20px; border-radius: 8px; background: ${C.emerald}; border: none; color: #fff; font-family: ${F.ui}; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .15s; }
  .bd-btn-primary:hover { background: #059669; }

  /* Lightweight loading modal */
  .bd-loading-overlay { position: fixed; inset: 0; z-index: 2000; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; }
  .bd-loading-box { background: #fff; border-radius: 14px; padding: 28px 36px; display: flex; flex-direction: column; align-items: center; gap: 14px; box-shadow: 0 24px 64px rgba(0,0,0,.2); }
  .bd-spinner { width: 30px; height: 30px; border-radius: 50%; border: 3px solid ${C.border}; border-top-color: ${C.indigo}; animation: bd-spin .8s linear infinite; }
  @keyframes bd-spin { to { transform: rotate(360deg); } }
  .bd-loading-label { font-size: 13px; font-weight: 600; color: ${C.slate}; }

  @media (max-width: 640px) {
    .bd-topbar { padding: 0 16px; }
    .bd-main { padding: 20px 14px 60px; }
    .bd-mini-bar-wrap { display: none; }
  }
`;

const COLOR = {
  navy: "#0B1D3A",
  navyMid: "#162d57",
  gold: "#C9A84C",
  goldLight: "#f0e0a8",
  bg: "#F7F5F0",
  cardBg: "#FFFFFF",
  slate: "#4A5568",
  muted: "#8896A5",
  border: "#DDE3EE",
  staffTag: "#1a56db",
  stuTag: "#0e7f57",
};

const FONT = {
  display: "'Playfair Display', Georgia, serif",
  body: "'Inter', system-ui, sans-serif",
};

const globalStyles = `
  * { box-sizing: border-box; }
  body { background: ${COLOR.bg}; font-family: ${FONT.body}; }
`;

// ─── Attendance colour ────────────────────────────────────────────
function attColor(pct) {
  if (pct >= 90) return C.emerald;
  if (pct >= 70) return C.amber;
  return C.rose;
}

// ─── Inline bar row ───────────────────────────────────────────────
function AttBar({ pct }) {
  const color = attColor(pct);
  return (
    <div className="bd-att-row">
      <div className="bd-att-track">
        <div
          className="bd-att-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="bd-att-pct" style={{ color }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
}

// ─── Batch panel (works for both manual & biometric records) ─────
function BatchPanel({ batch, filteredRecords, isOpen, onToggle, onDelete }) {
  const total = batch.records?.length || 0;
  const avg =
    filteredRecords.length > 0
      ? filteredRecords.reduce(
          (s, r) => s + Number(r.attendancePercent || 0) * 100,
          0,
        ) / filteredRecords.length
      : 0;

  return (
    <div className="bd-batch">
      <button className="bd-batch-header" onClick={onToggle}>
        <div className={`bd-batch-chevron ${isOpen ? "open" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 4l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="bd-batch-meta">
          <div className="bd-batch-date">
            📅 {batch.label}
            <span className={`bd-source-badge ${batch.source}`}>
              {batch.source === "manual" ? "Manual" : "Biometric"}
            </span>
          </div>
          <div className="bd-batch-count">
            {filteredRecords.length} of {total} records
            {filteredRecords.length < total && " (filtered)"}
          </div>
        </div>

        <div className="bd-mini-bar-wrap" onClick={(e) => e.stopPropagation()}>
          <div className="bd-mini-bar-lbl">
            <span>Avg Attendance</span>
            <span style={{ color: attColor(avg), fontWeight: 700 }}>
              {avg.toFixed(1)}%
            </span>
          </div>
          <div className="bd-mini-bar-track">
            <div
              className="bd-mini-bar-fill"
              style={{ width: `${avg}%`, background: attColor(avg) }}
            />
          </div>
        </div>
      </button>

      {isOpen && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {filteredRecords.length === 0 ? (
            <div className="bd-empty">
              <div className="bd-empty-icon">🔍</div>
              No records match current filters.
            </div>
          ) : (
            <div className="bd-table-wrap">
              <table className="bd-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>ID</th>
                    <th>Department</th>
                    <th>Total Days</th>
                    <th>Present Days</th>
                    <th>Absent Days</th>
                    <th>Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((s, i) => {
                    const pct = Number(s.attendancePercent || 0) * 100;
                    return (
                      <tr key={i}>
                        <td
                          style={{
                            color: C.muted,
                            fontVariantNumeric: "tabular-nums",
                            width: "40px",
                          }}
                        >
                          {s.srNo ?? i + 1}
                        </td>
                        <td style={{ fontWeight: 500, color: C.text }}>
                          {s.name}
                        </td>
                        <td
                          style={{ fontFamily: "monospace", fontSize: "12px" }}
                        >
                          {s.uniqueId}
                        </td>
                        <td>{s.department || "—"}</td>
                        <td>{s.totalDays}</td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>
                          {s.presentDays ?? "—"}
                        </td>
                        <td style={{ fontVariantNumeric: "tabular-nums" }}>
                          {s.absentDays ?? "—"}
                        </td>
                        <td className="bd-att-cell">
                          <AttBar pct={pct} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Normalizers ───────────────────────────────────────────────────
// Biometric batch doc → common batch shape
function normalizeBiometricBatch(id, raw) {
  return {
    id,
    source: "biometric",
    label: raw.uploadDate || id,
    timestamp: raw.timestamp || 0,
    rawKey: id, // batches.<id> for deletion
    records: (raw.records || []).map((r) => ({
      name: r.name || "",
      uniqueId: r.uniqueId || "",
      department: r.department || "",
      totalDays: r.totalDays,
      presentDays: r.presentDays,
      absentDays: r.absentDays,
      attendancePercent: Number(r.attendancePercent) || 0, // already a 0–1 fraction
      srNo: r.srNo,
    })),
  };
}

// Manual month entry → common batch shape
function normalizeManualBatch(month, records) {
  const timestamp = new Date(`${month}-01T00:00:00`).getTime() || 0;
  return {
    id: `manual_${month}`,
    source: "manual",
    label: `Manual Entry — ${month}`,
    timestamp,
    rawKey: month, // the field name to delete on the manual doc
    records: (records || []).map((r) => ({
      name: r.name || "",
      uniqueId: r.id || "",
      department: r.department || "",
      totalDays: r.totalDays,
      presentDays: r.presentDays,
      absentDays: r.absentDays,
      attendancePercent: (Number(r.attendancePercentage) || 0) / 100, // convert % → fraction
    })),
  };
}

// ─── Main component ───────────────────────────────────────────────
export default class AttendanceDashboardMerged extends Component {
  state = {
    Title: "PG Student Attendance Details",
    bioBatches: [],
    manualBatches: [],
    loadingBio: true,
    loadingManual: true,
    pendingStudents: [],
    showPreview: false,
    LoadingModal: false,
    search: "",
    selectedDepartment: "",
    minAttendance: 0,
    sourceFilter: "all", // "all" | "manual" | "biometric"
    openBatchIds: new Set(),
  };

  unsubscribeBio = null;
  unsubscribeManual = null;

  componentDidMount() {
    // Biometric (Excel import) source
    this.unsubscribeBio = onSnapshot(
      doc(db, BIO_COLLECTION, BIO_DOC_ID),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const batchesMap = data?.batches || {};
          const batchList = Object.keys(batchesMap).map((key) =>
            normalizeBiometricBatch(key, batchesMap[key]),
          );
          this.setState(
            { bioBatches: batchList, loadingBio: false },
            this.autoOpenNewest,
          );
        } else {
          this.setState({ bioBatches: [], loadingBio: false });
        }
      },
      (err) => {
        console.error("Biometric snapshot error:", err);
        this.setState({ loadingBio: false });
      },
    );

    // Manual entry source
    this.unsubscribeManual = onSnapshot(
      doc(db, MANUAL_COLLECTION, MANUAL_DOC_ID),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const batchList = Object.entries(data)
            .filter(([key]) => key !== "createdAt" && key !== "updatedAt")
            .map(([month, records]) =>
              normalizeManualBatch(
                month,
                Array.isArray(records) ? records : [],
              ),
            );
          this.setState(
            { manualBatches: batchList, loadingManual: false },
            this.autoOpenNewest,
          );
        } else {
          this.setState({ manualBatches: [], loadingManual: false });
        }
      },
      (err) => {
        console.error("Manual snapshot error:", err);
        this.setState({ loadingManual: false });
      },
    );
  }

  componentWillUnmount() {
    if (this.unsubscribeBio) this.unsubscribeBio();
    if (this.unsubscribeManual) this.unsubscribeManual();
  }

  // Auto-open the single newest batch across both sources (only once, first load)
  autoOpenNewest = () => {
    this.setState((prev) => {
      if (prev.openBatchIds.size > 0) return null;
      const combined = [...prev.bioBatches, ...prev.manualBatches].sort(
        (a, b) => b.timestamp - a.timestamp,
      );
      if (combined.length === 0) return null;
      return { openBatchIds: new Set([combined[0].id]) };
    });
  };

  getCombinedBatches = () => {
    const { bioBatches, manualBatches, sourceFilter } = this.state;
    let combined = [...bioBatches, ...manualBatches];
    if (sourceFilter === "manual") combined = manualBatches;
    if (sourceFilter === "biometric") combined = bioBatches;
    return combined.sort((a, b) => b.timestamp - a.timestamp);
  };

  toggleBatch = (id) => {
    this.setState((prev) => {
      const s = new Set(prev.openBatchIds);
      s.has(id) ? s.delete(id) : s.add(id);
      return { openBatchIds: s };
    });
  };

  handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    this.setState({ LoadingModal: true });
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, range: 5 });
        const formatted = jsonData
          .map((row, i) => ({
            srNo: i + 1,
            originalSrNo: row[0],
            name: (row[2] || "").toString().trim(),
            uniqueId: (row[3] || "").toString().trim(),
            department: (row[5] || "").toString().trim(),
            month: row[6] || "",
            totalDays: row[7],
            workingDays: row[8],
            presentDays: row[9],
            absentDays: row[10],
            totalHours: row[11],
            attendancePercent: parseFloat(row[12]) || 0,
          }))
          .filter((s) => s.name);
        this.setState({ pendingStudents: formatted, showPreview: true });
      } catch (err) {
        console.error(err);
        alert("Error reading Excel file. Please check the file format.");
      } finally {
        this.setState({ LoadingModal: false });
        e.target.value = "";
      }
    };
    reader.readAsBinaryString(file);
  };

  handleUploadBatch = async () => {
    if (!this.state.pendingStudents.length) {
      alert("No records found");
      return;
    }
    this.setState({ LoadingModal: true });
    try {
      const now = new Date();
      const batchId = `batch_${now.getTime()}`;
      const newBatch = {
        uploadDate: now.toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
        timestamp: now.getTime(),
        fileName: `Biometric_Import_${now.toISOString().slice(0, 10)}`,
        totalRecords: this.state.pendingStudents.length,
        records: [...this.state.pendingStudents],
      };
      await setDoc(
        doc(db, BIO_COLLECTION, BIO_DOC_ID),
        { batches: { [batchId]: newBatch } },
        { merge: true },
      );
      this.setState({ pendingStudents: [], showPreview: false });
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed. Check console.");
    } finally {
      this.setState({ LoadingModal: false });
    }
  };

  handleDeleteBatch = async (batch) => {
    if (
      !window.confirm(
        `Delete this entire ${batch.source === "manual" ? "month" : "batch"}?`,
      )
    )
      return;
    try {
      if (batch.source === "biometric") {
        await updateDoc(doc(db, BIO_COLLECTION, BIO_DOC_ID), {
          [`batches.${batch.rawKey}`]: deleteField(),
        });
      } else {
        await updateDoc(doc(db, MANUAL_COLLECTION, MANUAL_DOC_ID), {
          [batch.rawKey]: deleteField(),
        });
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete.");
    }
  };

  getFilteredBatchRecords = (batch) => {
    const { search, selectedDepartment, minAttendance } = this.state;
    return (batch.records || []).filter((s) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (s.name || "").toLowerCase().includes(q) ||
        String(s.uniqueId || "")
          .toLowerCase()
          .includes(q);
      const matchDept =
        !selectedDepartment ||
        (s.department || "").trim().toLowerCase() ===
          selectedDepartment.trim().toLowerCase();
      const pct = Number(s.attendancePercent || 0) * 100;
      return matchSearch && matchDept && pct >= minAttendance;
    });
  };

  getAllFiltered = () => {
    return this.getCombinedBatches().flatMap((b) =>
      this.getFilteredBatchRecords(b).map((r) => ({
        ...r,
        batchId: b.id,
        source: b.source,
      })),
    );
  };

  render() {
    const {
      loadingBio,
      loadingManual,
      search,
      selectedDepartment,
      minAttendance,
      sourceFilter,
      openBatchIds,
      showPreview,
      pendingStudents,
      LoadingModal,
    } = this.state;

    const combinedBatches = this.getCombinedBatches();
    const allFiltered = this.getAllFiltered();
    const totalStaff = allFiltered.length;
    const avgAtt =
      totalStaff > 0
        ? (
            allFiltered.reduce(
              (s, r) => s + Number(r.attendancePercent || 0) * 100,
              0,
            ) / totalStaff
          ).toFixed(1)
        : "—";
    const lowCount = allFiltered.filter(
      (r) => Number(r.attendancePercent || 0) * 100 < 70,
    ).length;

    const departments = [
      ...new Set(
        combinedBatches.flatMap(
          (b) => b.records?.map((r) => (r.department || "").trim()) || [],
        ),
      ),
    ]
      .filter(Boolean)
      .sort();

    const loading = loadingBio || loadingManual;

    return (
      <div
        style={{
          fontFamily: FONT.body,
          background: COLOR.bg,
          minHeight: "100vh",
        }}
      >
        <style>{globalStyles}</style>
        <Header />
        <div className="bd-page" style={{ marginTop: "6rem" }}>
          <style>{CSS}</style>

          {/* NDC banner header */}
          <div className="bd-banner">
            <p className="bd-banner-eyebrow">NDC · Academic Directory</p>
            <h1 className="bd-banner-title">PG Student Attendance Details</h1>
          </div>

          {/* Top bar */}
          <div className="bd-topbar">
            <div>
              <div className="bd-topbar-title">{this.state.Title}</div>
              <div className="bd-topbar-sub">
                Manual entries & biometric imports, combined
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
              <div className="bd-stat">
                <span className="bd-stat-val" style={{ color: "#fff" }}>
                  {totalStaff}
                </span>
                <span className="bd-stat-lbl">Records</span>
              </div>
              <div className="bd-stat">
                <span
                  className="bd-stat-val"
                  style={{
                    color:
                      avgAtt !== "—" && parseFloat(avgAtt) < 70
                        ? "#FCA5A5"
                        : "#6EE7B7",
                  }}
                >
                  {avgAtt}
                  {avgAtt !== "—" ? "%" : ""}
                </span>
                <span className="bd-stat-lbl">Avg Att.</span>
              </div>
              <div className="bd-stat">
                <span
                  className="bd-stat-val"
                  style={{
                    color: lowCount > 0 ? "#FCA5A5" : "rgba(255,255,255,.4)",
                  }}
                >
                  {lowCount}
                </span>
                <span className="bd-stat-lbl">Below 70%</span>
              </div>

              <label className="bd-import-btn">
                <svg width="14" height="14" fill="none" viewBox="0 0 14 14">
                  <path
                    d="M7 1v8M4 6l3 3 3-3M1 10v1.5A1.5 1.5 0 0 0 2.5 13h9A1.5 1.5 0 0 0 13 11.5V10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Import Excel
                <input
                  type="file"
                  hidden
                  accept=".xlsx"
                  onChange={this.handleImport}
                />
              </label>
            </div>
          </div>

          <div className="bd-main">
            {/* Filters */}
            <div className="bd-filters">
              <input
                className="bd-input"
                type="text"
                placeholder="Search by name or ID…"
                value={search}
                onChange={(e) => this.setState({ search: e.target.value })}
              />
              <select
                className="bd-select"
                value={selectedDepartment}
                onChange={(e) =>
                  this.setState({ selectedDepartment: e.target.value })
                }
              >
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <div className="bd-range-wrap">
                <div className="bd-range-lbl">
                  <span>Min attendance</span>
                  <span style={{ color: C.indigo }}>{minAttendance}%</span>
                </div>
                <input
                  className="bd-range"
                  type="range"
                  min="0"
                  max="100"
                  value={minAttendance}
                  onChange={(e) =>
                    this.setState({ minAttendance: Number(e.target.value) })
                  }
                />
              </div>
              <div className="bd-source-toggle">
                <button
                  className={`bd-source-chip ${sourceFilter === "all" ? "active" : ""}`}
                  onClick={() => this.setState({ sourceFilter: "all" })}
                >
                  All Sources
                </button>
                <button
                  className={`bd-source-chip ${sourceFilter === "manual" ? "active" : ""}`}
                  onClick={() => this.setState({ sourceFilter: "manual" })}
                >
                  Manual
                </button>
                <button
                  className={`bd-source-chip ${sourceFilter === "biometric" ? "active" : ""}`}
                  onClick={() => this.setState({ sourceFilter: "biometric" })}
                >
                  Biometric
                </button>
              </div>
            </div>

            {/* Batches */}
            <div className="bd-section-head">
              <span className="bd-section-title">
                Attendance Records ({combinedBatches.length})
              </span>
            </div>

            {loading ? (
              <div
                className="bd-empty"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: "12px",
                }}
              >
                <div className="bd-empty-icon">⏳</div>
                <p style={{ fontWeight: 600, color: C.slate }}>
                  Loading attendance records…
                </p>
              </div>
            ) : combinedBatches.length === 0 ? (
              <div
                className="bd-empty"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: "12px",
                }}
              >
                <div className="bd-empty-icon">📂</div>
                <p style={{ fontWeight: 600, color: C.slate }}>
                  No attendance records yet
                </p>
                <p style={{ fontSize: "13px", marginTop: "6px" }}>
                  Import an Excel file, or add records manually, to get started
                </p>
              </div>
            ) : (
              combinedBatches.map((batch) => (
                <BatchPanel
                  key={batch.id}
                  batch={batch}
                  filteredRecords={this.getFilteredBatchRecords(batch)}
                  isOpen={openBatchIds.has(batch.id)}
                  onToggle={() => this.toggleBatch(batch.id)}
                  onDelete={() => this.handleDeleteBatch(batch)}
                />
              ))
            )}
          </div>

          {/* Preview Modal */}
          {showPreview && (
            <div className="bd-modal-overlay">
              <div className="bd-modal">
                <div className="bd-modal-head">
                  <div>
                    <div className="bd-modal-title">Preview Import</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: C.muted,
                        marginTop: "2px",
                      }}
                    >
                      {pendingStudents.length} records ready to upload
                    </div>
                  </div>
                  <button
                    className="bd-modal-close"
                    onClick={() =>
                      this.setState({ showPreview: false, pendingStudents: [] })
                    }
                  >
                    ✕
                  </button>
                </div>
                <div className="bd-modal-body">
                  <div className="bd-table-wrap">
                    <table className="bd-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>ID</th>
                          <th>Department</th>
                          <th>Total Days</th>
                          <th>Present Days</th>
                          <th>Absent Days</th>
                          <th>Attendance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingStudents.map((s, i) => {
                          const pct = s.attendancePercent * 100;
                          return (
                            <tr key={i}>
                              <td
                                style={{
                                  color: C.muted,
                                  fontVariantNumeric: "tabular-nums",
                                  width: "40px",
                                }}
                              >
                                {s.srNo}
                              </td>
                              <td style={{ fontWeight: 500, color: C.text }}>
                                {s.name}
                              </td>
                              <td
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "12px",
                                }}
                              >
                                {s.uniqueId}
                              </td>
                              <td>{s.department || "—"}</td>
                              <td>{s.totalDays}</td>
                              <td
                                style={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {s.presentDays ?? "—"}
                              </td>
                              <td
                                style={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {s.absentDays ?? "—"}
                              </td>
                              <td className="bd-att-cell">
                                <AttBar pct={pct} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="bd-modal-foot">
                  <button
                    className="bd-btn-secondary"
                    onClick={() =>
                      this.setState({ showPreview: false, pendingStudents: [] })
                    }
                  >
                    Cancel
                  </button>
                  <button
                    className="bd-btn-primary"
                    onClick={this.handleUploadBatch}
                  >
                    Upload Batch
                  </button>
                </div>
              </div>
            </div>
          )}

          {LoadingModal && (
            <div className="bd-loading-overlay">
              <div className="bd-loading-box">
                <div className="bd-spinner" />
                <div className="bd-loading-label">Processing...</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
